import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useSupabase } from '../contexts/SupabaseContext';
import UserNavBar from '../components/UserNavBar';
import { Submission } from '../lib/supabaseClient';
import withIdleTimer from '../hoc/withIdleTimer';
import TempusUserGuide from '../components/TempusUserGuide';
import { importMarkdown } from '../utils/markdownUtils';
import { isContinuingReviewsUiEnabled } from '../services/featureFlagService';

// Unique ID to track component instances
const COMPONENT_ID = `UserDashboard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const REBRAND_BANNER_STORAGE_KEY = 'timely-rebrand-banner-dismissed';

const UserDashboard: React.FC = () => {
  console.debug(`[${COMPONENT_ID}] Component rendering at ${new Date().toISOString()}`);

  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [submissions, setSubmissions] = React.useState<Submission[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [userGuideContent, setUserGuideContent] = useState<string>('');
  const [showRebrandBanner, setShowRebrandBanner] = useState(true);

  // Add a ref to track if a fetch is in progress
  const isFetchingRef = useRef(false);

  // Log auth state on every render
  console.debug(`[${COMPONENT_ID}] Auth State: { authLoading: ${authLoading}, userId: ${user?.id || 'null'} } at ${new Date().toISOString()}`);
  // Log submissions loading state on every render
  console.debug(`[${COMPONENT_ID}] Submissions Loading State: { isLoading: ${isLoading}, submissionsCount: ${submissions.length} } at ${new Date().toISOString()}`);

  // Memoize the Supabase client
  const supabase = useSupabase();

  // Memoize the fetch function to avoid recreating it on each render
  const fetchSubmissions = useCallback(async () => {
    // Skip if already fetching
    if (isFetchingRef.current) {
      console.debug(`[${COMPONENT_ID}] fetchSubmissions already in progress, skipping duplicate call at ${new Date().toISOString()}`);
      return;
    }

    // Skip if user is not available
    if (!user) {
      console.debug(`[${COMPONENT_ID}] fetchSubmissions called with no user, skipping`);
      return;
    }

    const userId = user.id;
    const fetchStartTime = new Date().toISOString();
    console.debug(`[${COMPONENT_ID}] fetchSubmissions started at ${fetchStartTime} for userId: ${userId}`);

    isFetchingRef.current = true;
    setIsLoading(true);
    console.debug(`[${COMPONENT_ID}] fetchSubmissions set isLoading=true at ${new Date().toISOString()}`);

    console.debug(`[${COMPONENT_ID}] fetchSubmissions executing supabase query for userId: ${userId}`);
    try {
      const queryStartTime = performance.now();
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .order('created_at', { ascending: false });
      const queryEndTime = performance.now();

      console.debug(`[${COMPONENT_ID}] fetchSubmissions supabase query completed in ${(queryEndTime - queryStartTime).toFixed(2)}ms`);

      if (error) {
        console.error(`[${COMPONENT_ID}] Error fetching submissions: [${error.code}] ${error.message}`, error);
        return;
      }

      console.debug(`[${COMPONENT_ID}] fetchSubmissions successful, got ${data?.length || 0} submissions`);
      setSubmissions((data || []) as Submission[]);
      console.debug(`[${COMPONENT_ID}] fetchSubmissions set submissions state at ${new Date().toISOString()}`);
    } catch (err) {
      console.error(`[${COMPONENT_ID}] Unexpected error in fetchSubmissions:`, err);
    } finally {
      setIsLoading(false);
      console.debug(`[${COMPONENT_ID}] fetchSubmissions set isLoading=false at ${new Date().toISOString()}`);

      isFetchingRef.current = false;
      const fetchEndTime = new Date().toISOString();
      console.debug(`[${COMPONENT_ID}] fetchSubmissions completed at ${fetchEndTime} (${Date.parse(fetchEndTime) - Date.parse(fetchStartTime)}ms total)`);
    }
  }, [user, supabase]);

  // Update the dependency array to only include user?.id instead of user and remove authLoading
  React.useEffect(() => {
    console.debug(`[${COMPONENT_ID}] useEffect triggered with userId: ${user?.id || 'null'} at ${new Date().toISOString()}`);

    // Only fetch if we have a user ID and auth loading is done
    if (!authLoading && user?.id) {
      console.debug(`[${COMPONENT_ID}] Calling fetchSubmissions for userId: ${user.id}`);
      fetchSubmissions();
    } else {
      console.debug(`[${COMPONENT_ID}] Skipping fetchSubmissions because ${authLoading ? 'authLoading=true' : 'user=null'} at ${new Date().toISOString()}`);
    }
  }, [user?.id, fetchSubmissions, authLoading]);

  // Load the user guide markdown content
  useEffect(() => {
    const loadUserGuide = async () => {
      try {
        const content = await importMarkdown('/assets/TempusUserGuide.md');
        setUserGuideContent(content);
      } catch (error) {
        console.error('Error loading user guide:', error);
        setUserGuideContent('# Error Loading User Guide\nPlease refresh the page or contact support.');
      }
    };

    loadUserGuide();
  }, []);

  useEffect(() => {
    setShowRebrandBanner(localStorage.getItem(REBRAND_BANNER_STORAGE_KEY) !== 'true');
  }, []);

  const dismissRebrandBanner = () => {
    localStorage.setItem(REBRAND_BANNER_STORAGE_KEY, 'true');
    setShowRebrandBanner(false);
  };

  const getStatusColor = (status: string) => {
    const colors = {
      DRAFT: 'bg-gray-200',
      SUBMISSION: 'bg-blue-200',
      ADMINISTRATIVE_REVIEW: 'bg-yellow-200',
      IRB_REVIEW: 'bg-purple-200',
      DECISION_ISSUED: 'bg-green-200'
    };
    // The amber NEEDS_ACTION pill is a continuing-reviews surface; with the flag
    // off this falls through to the original default gray, as on origin/main.
    if (isContinuingReviewsUiEnabled() && status === 'NEEDS_ACTION') {
      return 'bg-amber-200';
    }
    return colors[status as keyof typeof colors] || 'bg-gray-200';
  };

  const getTypeLabel = (type?: string) => {
    switch (type) {
      case 'CONTINUING_REVIEW':
        return 'Continuing Review';
      case 'AMENDMENT':
        return 'Amendment';
      default:
        return 'Initial Submission';
    }
  };

  const ownedSubmissions = React.useMemo(
    () => submissions.filter((submission) => submission.user_id === user?.id),
    [submissions, user?.id]
  );

  const sharedSubmissions = React.useMemo(
    () => submissions.filter((submission) => submission.user_id !== user?.id),
    [submissions, user?.id]
  );

  const renderSubmissionCards = (items: Submission[], isSharedSection: boolean = false) => {
    // The submission-type badge (and the title reshuffle it required) is a
    // continuing-reviews surface. With the flag off, render the original card
    // markup: the protocol title sits in the flex row, with no type badge.
    const crUiEnabled = isContinuingReviewsUiEnabled();
    return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {items.map((submission) => (
        <div
          key={submission.id}
          className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6 flex flex-col"
        >
          <div className="flex-1">
            <div className={`w-full h-2 ${getStatusColor(submission.status)} rounded-t-lg -mt-6 -mx-6 mb-4`}></div>
            {crUiEnabled ? (
              <>
                <div className="flex items-start justify-between gap-3">
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700">
                    {getTypeLabel(submission.submission_type)}
                  </span>
                  {isSharedSection && (
                    <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                      Read-only
                    </span>
                  )}
                </div>
                <h3 className="text-lg font-semibold mt-3">
                  {submission.data && typeof submission.data === 'object' && 'protocol_title' in submission.data
                    ? (submission.data as any).protocol_title
                    : 'Unnamed Study Protocol'}
                </h3>
              </>
            ) : (
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-lg font-semibold">
                  {submission.data && typeof submission.data === 'object' && 'protocol_title' in submission.data
                    ? (submission.data as any).protocol_title
                    : 'Unnamed Study Protocol'}
                </h3>
                {isSharedSection && (
                  <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                    Read-only
                  </span>
                )}
              </div>
            )}
            <p className="text-md text-gray-500 mt-3">
              {submission.data && typeof submission.data === 'object' &&
                'pi_first_name' in submission.data && 'pi_last_name' in submission.data
                ? `PI: ${(submission.data as any).pi_first_name} ${(submission.data as any).pi_last_name}`
                : 'PI: Not specified'}
            </p>
            {isSharedSection && (
              <p className="mt-2 text-sm text-gray-500">
                Shared with you for review only.
              </p>
            )}
          </div>
          <div className="mt-3 pt-4 flex items-center justify-between border-t border-gray-100">
            <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(submission.status)}`}>
              {submission.status.replace(/_/g, ' ')}
            </span>
            <button
              onClick={() => navigate(`/submissions/${submission.id}`)}
              className="text-[#F55D3E] hover:text-[#F9A594] text-md font-medium"
            >
              View Details →
            </button>
          </div>
        </div>
      ))}
    </div>
    );
  };

  const handleLogout = async () => {
    console.debug(`[${COMPONENT_ID}] handleLogout called at ${new Date().toISOString()}`);
    try {
      await signOut();
      console.debug(`[${COMPONENT_ID}] signOut successful, navigating to /auth`);
      navigate('/signin');
    } catch (error) {
      console.error(`[${COMPONENT_ID}] Error signing out:`, error);
    }
  };

  // Show loading state while auth is initializing or submissions are loading
  if (authLoading || isLoading) {
    console.debug(`[${COMPONENT_ID}] Rendering loading state: { authLoading: ${authLoading}, isLoading: ${isLoading} } at ${new Date().toISOString()}`);
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} hideDashboardLink={true} />
        <div className="flex-1 bg-gray-50 mt-16">
          <div className="p-8 text-center">
            <p className="text-gray-500">Loading your submissions...refresh your browser tab if this takes too long.</p>
          </div>
        </div>
      </div>
    );
  }

  console.debug(`[${COMPONENT_ID}] Rendering dashboard with ${submissions.length} submissions at ${new Date().toISOString()}`);
  return (
    <div className="flex flex-col h-screen">
      <UserNavBar user={user} onSignOut={handleLogout} hideDashboardLink={true} />

      {/* Main Content - Adjusted for top navbar */}
      <div className="flex-1 bg-gray-50 mt-16">
        <div className="p-8">
          <div className="flex justify-between items-center mb-8">
            <h1 className="text-2xl font-bold">My Submissions</h1>
            <button
              onClick={() => navigate('/new-submission')}
              className="bg-[#F55D3E] text-white px-6 py-2 rounded-lg hover:bg-[#F9A594] transition-colors"
            >
              Submit A New Study
            </button>
          </div>

          {showRebrandBanner && (
            <div
              className="mb-8 rounded-lg border border-[#F55D3E]/20 bg-white p-5 shadow-sm"
              role="status"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="space-y-2">
                  <p className="text-base font-semibold text-gray-900">
                    Tempus IRB is now Timely IRB.
                  </p>
                  <p className="max-w-3xl text-sm leading-6 text-gray-600">
                    Your account, submissions, approvals, and review process are unchanged. Existing Tempus IRB links will continue to route here during the transition; new emails and documents will use Timely IRB.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={dismissRebrandBanner}
                  className="self-start rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:border-[#F55D3E]/40 hover:text-[#F55D3E]"
                  aria-label="Dismiss rebrand announcement"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {ownedSubmissions.length === 0 && sharedSubmissions.length === 0 ? (
            <>
              <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                No submissions yet. Click "Submit A New Study" to get started.
              </div>

              {/* User Guide - Expanded when no submissions */}
              {userGuideContent && (
                <TempusUserGuide
                  markdownContent={userGuideContent}
                  defaultExpanded={true}
                />
              )}
            </>
          ) : (
            <>
              <div className="space-y-10">
                <section>
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">My Submissions</h2>
                    <p className="text-sm text-gray-500">You can view and manage submissions you created here.</p>
                  </div>
                  {ownedSubmissions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-gray-500">
                      You do not have any submissions you own yet.
                    </div>
                  ) : (
                    renderSubmissionCards(ownedSubmissions)
                  )}
                </section>

                <section>
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold text-gray-900">Shared With Me</h2>
                    <p className="text-sm text-gray-500">These submissions were shared with you for read-only review.</p>
                  </div>
                  {sharedSubmissions.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-gray-500">
                      No submissions have been shared with you.
                    </div>
                  ) : (
                    renderSubmissionCards(sharedSubmissions, true)
                  )}
                </section>
              </div>

              {/* User Guide - Collapsed when submissions exist */}
              {userGuideContent && (
                <TempusUserGuide
                  markdownContent={userGuideContent}
                  defaultExpanded={false}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default withIdleTimer(UserDashboard); 
